import { Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';
import { UserConfigLotService } from '../user-config-lot.service';
import { LotteryAccessService } from '../../../shared/services/lottery-access.service';
import { AuthService } from '../../../shared/services/auth.service';

@Component({
  selector: 'app-edit-permission',
  templateUrl: './edit-permission.modal.component.html',
  styleUrls: ['./edit-permission.modal.component.scss'],
})
export class EditPermissionModalComponent implements OnInit {
  @Input() user: any;
  @ViewChild('requestTOTPCode') validateToken!: TemplateRef<any>;

  public isLoading = false;
  public permissions: any = null;
  public isLottery = false;

  public validationCode = '';
  public validatingTotp = false;
  public errorMessage = '';

  public permissionLotteryOptions = [
    { key: 'overview', label: 'Visão Geral' },
    { key: 'transactionalAccounts', label: 'Contas Transacionais' },
    { key: 'operationIndicators', label: 'Indicadores de Operação' },
    { key: 'product', label: 'Produtos' },
    { key: 'operators', label: 'Operadores' },
    { key: 'usersIndicators', label: 'Indicadores de Usuários' },
    { key: 'playerList', label: 'Lista de Jogadores' },
    { key: 'monitoring', label: 'Monitoramento' },
    { key: 'financeIndicators', label: 'Indicadores de Finanças' },
    { key: 'operatorList', label: 'Lista de Operadores' },
    { key: 'financialSummary', label: 'Resumo Financeiro' },
    { key: 'betSummary', label: 'Resumo de Apostas' },
    { key: 'userConfigLot', label: 'Usuários Loteria' },
    { key: 'activityLog', label: 'Log de Usuários' },
  ];

  public permissionOperatorOptions = [
    { key: 'reports', label: 'Dashboard' },
    { key: 'subAccounts', label: 'Subcontas' },
    { key: 'extrato', label: 'Movimentações' },
    { key: 'resumo', label: 'Resumo' },
    { key: 'infracoes', label: 'Infrações' },
    { key: 'deposito', label: 'Depósito QR Code' },
    { key: 'depositoPix', label: 'Depósito Chave Pix' },
    { key: 'transferencia', label: 'Saque Chave Pix' },
    { key: 'saqueCopiaCola', label: 'Saque Copia e Cola' },
    { key: 'saqueBoleto', label: 'Saque Boleto' },
    { key: 'transferBalance', label: 'Transferir Saldo' },
    { key: 'approveWithdraw', label: 'Aprovar Saque' },
    { key: 'accountView', label: 'Informações Conta' },
    { key: 'webhook', label: 'Webhook' },
    { key: 'userConfig', label: 'Usuários' },
    { key: 'technicalEvents', label: 'Log de Integração' },
  ];

  public permissionColumns: any[][] = [];
  public permissionsForm = new FormGroup({});

  public accountAdm = false;

  public accountOptions: any[] = [];
  public selectedAccounts = new FormControl<string[]>([], { nonNullable: true });
  private totpModalRef: NgbModalRef | null = null;

  private permissionsOperatorDefaults: Record<string, boolean> = {
    reports: false, subAccounts: false, extrato: false, resumo: false,
    infracoes: false, comissoes: false, carteiraComissoes: false,
    deposito: false, depositoPix: false, transferencia: false,
    saqueCopiaCola: false, saqueBoleto: false, transferBalance: false,
    approveWithdraw: false, reservaFinanceira: false, criptomoeda: false,
    wallet: false, accountView: false, webhook: false, userConfig: false,
    bankingCredentials: false, technicalEvents: false, apiCredentials: false,
  };

  private permissionsLotteryDefaults: Record<string, boolean> = {
    overview: false, transactionalAccounts: false, operationIndicators: false,
    product: false, operators: false, usersIndicators: false,
    playerList: false, monitoring: false, financeIndicators: false,
    operatorList: false, financialSummary: false, betSummary: false,
    userConfigLot: false, activityLog: false,
  };

  constructor(
    public activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private toastr: ToastrService,
    private userConfigLotService: UserConfigLotService,
    private lotteryAccessService: LotteryAccessService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.detectAccountType();
  }

  dismiss(): void {
    this.totpModalRef?.dismiss();
    this.activeModal.dismiss();
  }

  toggleAccount(id: string): void {
    const current = this.selectedAccounts.value;
    if (current.includes(id)) {
      this.selectedAccounts.setValue(current.filter(v => v !== id));
    } else {
      this.selectedAccounts.setValue([...current, id]);
    }
  }

  onSaveClick(): void {
    this.validationCode = '';
    this.errorMessage = '';
    this.totpModalRef = this.modalService.open(this.validateToken, {
      backdrop: 'static',
      keyboard: true,
      centered: true,
      size: 'sm',
    });

    this.totpModalRef.closed.subscribe(() => (this.totpModalRef = null));
    this.totpModalRef.dismissed.subscribe(() => (this.totpModalRef = null));
  }

  async pasteTotpCode(): Promise<void> {
    try {
      const text = await navigator.clipboard.readText();
      this.validationCode = text.trim();
    } catch {
      this.toastr.error('Erro ao colar o código do clipboard');
    }
  }

  async validateTotp(): Promise<void> {
    if (this.validatingTotp) return;
    this.validatingTotp = true;

    const isValid = await this.authService.validateTotpCode(this.validationCode).then(r => r.isValid);

    if (isValid) {
      this.errorMessage = '';
      this.validationCode = '';
      this.totpModalRef?.close();
      this.doSave();
    } else {
      this.errorMessage = 'Código de autenticação inválido!';
    }

    this.validatingTotp = false;
  }

  private doSave(): void {
    this.isLoading = true;

    const formPerms = this.permissionsForm.getRawValue();

    const permissions = this.isLottery
      ? { ...this.permissionsOperatorDefaults }
      : { ...this.permissionsOperatorDefaults, ...formPerms };

    const permissionsLottery = this.isLottery
      ? { ...this.permissionsLotteryDefaults, ...formPerms }
      : { ...this.permissionsLotteryDefaults };

    const payload: any = {
      userId: this.user.email,
      permissions,
      permissionsLottery,
      accountMult: this.selectedAccounts.value,
      accountAdm: this.accountAdm,
    };

    this.userConfigLotService.updateUserPermissions(payload)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: () => {
          this.toastr.success('Permissões atualizadas com sucesso!', 'Sucesso');
          this.activeModal.close();
        },
        error: (err) => {
          const msg = err?.error?.mensagem || 'Erro ao atualizar permissões.';
          this.toastr.error(msg, 'Erro');
        },
      });
  }

  private async detectAccountType(): Promise<void> {
    this.isLoading = true;

    try {
      const account = await this.lotteryAccessService.getAccountCached();
      this.isLottery = this.lotteryAccessService.isLotteryManager(account);
    } catch {
      this.isLottery = true;
    }

    this.buildForm();
    this.loadPermissions();
  }

  private buildForm(): void {
    const options = this.isLottery
      ? this.permissionLotteryOptions
      : this.permissionOperatorOptions;

    const group: Record<string, FormControl<boolean>> = {};
    for (const opt of options) {
      group[opt.key] = new FormControl<boolean>(false, { nonNullable: true });
    }

    this.permissionsForm = new FormGroup(group);
    this.permissionColumns = this.chunkPermissions(options, 6);
  }

  private chunkPermissions(list: any[], size: number): any[][] {
    const chunks: any[][] = [];
    for (let i = 0; i < list.length; i += size) {
      chunks.push(list.slice(i, i + size));
    }
    return chunks;
  }

  loadPermissions(): void {
    if (!this.user?.email) {
      this.isLoading = false;
      return;
    }

    this.userConfigLotService
      .getUserPermissions(this.user.email, 10, 1)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (value: any) => {
          this.permissions = value;
          this.patchPermissions(value);
        },
        error: () => {
          this.toastr.error('Erro ao carregar permissões.', 'Erro', {
            timeOut: 3000,
            positionClass: 'toast-top-right',
          });
        },
      });
  }

  private patchPermissions(data: any): void {
    const item = Array.isArray(data?.items) ? data.items[0] : null;
    if (!item) return;

    const perms = this.isLottery
      ? item.permissionsLottery
      : item.permissionsOperator;

    if (perms && typeof perms === 'object') {
      this.permissionsForm.patchValue(perms);
    }

    this.accountAdm = item.accountAdm === true || item.accountAdm === 'true';
    this.patchAccounts(data);
  }

  private patchAccounts(data: any): void {
    const allAccounts: any[] = Array.isArray(data?.accountMultUser) ? data.accountMultUser : [];
    const selectedAccounts: any[] = Array.isArray(data?.accountMult) ? data.accountMult : [];

    this.accountOptions = allAccounts.map((a: any) => ({
      id: (a?.id ?? '').toString(),
      name: (a?.name ?? '').toString(),
    }));

    const validIds = new Set(this.accountOptions.map((a: any) => a.id));
    const selectedIds = selectedAccounts
      .map((a: any) => (a?.id ?? '').toString())
      .filter((id: string) => validIds.has(id));
    this.selectedAccounts.setValue(selectedIds);
  }
}
